import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Building2, Plus, Edit2, Trash2, Server, Users, X, Activity, Copy, Link2, Eye, EyeOff, ShoppingBag, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const REGISTER_HOST = 'daftar.kumarahotspot.com';
const getRegisterUrl = (slug) => `https://${REGISTER_HOST}/${slug}`;
const copyRegisterLink = (slug) => {
  if (!slug) {
    toast.error('Slug cabang belum tersedia. Simpan ulang cabang untuk generate slug.');
    return;
  }
  const url = getRegisterUrl(slug);
  navigator.clipboard.writeText(url).then(
    () => toast.success('Link disalin: ' + url),
    () => toast.error('Gagal menyalin link'),
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Branches = () => {
 const [branches, setBranches] = useState([]);
 const [loading, setLoading] = useState(true);
 const [showAddModal, setShowAddModal] = useState(false);
 const [editingBranch, setEditingBranch] = useState(null);
 const [showDeleteModal, setShowDeleteModal] = useState(false);
 const [branchToDelete, setBranchToDelete] = useState(null);
 const [saving, setSaving] = useState(false);

 const [formData, setFormData] = useState({
 name: '',
 address: '',
 city: '',
 latitude: '',
 longitude: '',
 description: '',
 is_active: true
 });

 useEffect(() => {
 fetchBranches();
 }, []);

 const fetchBranches = async () => {
 try {
 setLoading(true);
 const token = localStorage.getItem('token');
 const response = await axios.get(`${API_URL}/api/branches`, {
 headers: { Authorization: `Bearer ${token}` }
 });

 if (response.data.success) {
 setBranches(response.data.data || []);
 }
 } catch (error) {
 console.error('Error fetching branches:', error);
 toast.error('Gagal memuat data cabang');
 } finally {
 setLoading(false);
 }
 };

 const resetForm = () => {
 setFormData({
 name: '',
 address: '',
 city: '',
 latitude: '',
 longitude: '',
 description: '',
 is_active: true
 });
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setSaving(true);

 try {
 const token = localStorage.getItem('token');
 
 if (editingBranch) {
 const response = await axios.put(
 `${API_URL}/api/branches/${editingBranch.id}`,
 formData,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('Cabang berhasil diupdate');
 setShowAddModal(false);
 setEditingBranch(null);
 resetForm();
 await fetchBranches();
 }
 } else {
 const response = await axios.post(
 `${API_URL}/api/branches`,
 formData,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('Cabang berhasil ditambahkan');
 setShowAddModal(false);
 resetForm();
 await fetchBranches();
 }
 }
 } catch (error) {
 console.error('Error saving branch:', error);
 toast.error(error.response?.data?.detail || 'Gagal menyimpan cabang');
 } finally {
 setSaving(false);
 }
 };

 const togglePublicStore = async (branch) => {
 const current = branch.is_public_store === undefined ? true : !!branch.is_public_store;
 const next = !current;
 try {
 const token = localStorage.getItem('token');
 await axios.put(`${API_URL}/api/branches/${branch.id}`,
 { is_public_store: next },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 toast.success(next
 ? `Cabang "${branch.name}" TAMPIL di Voucher Store`
 : `Cabang "${branch.name}" disembunyikan dari Voucher Store`);
 fetchBranches();
 } catch (error) {
 toast.error('Gagal mengubah status voucher store');
 }
 };

 const toggleWhatsApp = async (branch) => {
 const current = branch.wa_enabled === undefined ? true : !!branch.wa_enabled;
 const next = !current;
 try {
 const token = localStorage.getItem('token');
 await axios.put(`${API_URL}/api/branches/${branch.id}`,
 { wa_enabled: next },
 { headers: { Authorization: `Bearer ${token}` } }
 );
 toast.success(next
 ? `Notifikasi WA cabang "${branch.name}" DIAKTIFKAN`
 : `Notifikasi WA cabang "${branch.name}" DIMATIKAN`);
 fetchBranches();
 } catch (error) {
 toast.error('Gagal mengubah status notifikasi WA');
 }
 };

 const handleEdit = (branch) => {
 setEditingBranch(branch);
 setFormData({
 name: branch.name,
 address: branch.address || '',
 city: branch.city || '',
 latitude: branch.latitude != null ? String(branch.latitude) : '',
 longitude: branch.longitude != null ? String(branch.longitude) : '',
 description: branch.description || '',
 is_active: branch.is_active
 });
 setShowAddModal(true);
 };

 const handleDelete = async () => {
 if (!branchToDelete) return;

 try {
 const token = localStorage.getItem('token');
 const response = await axios.delete(
 `${API_URL}/api/branches/${branchToDelete.id}`,
 { headers: { Authorization: `Bearer ${token}` } }
 );

 if (response.data.success) {
 toast.success('Cabang berhasil dihapus');
 setShowDeleteModal(false);
 setBranchToDelete(null);
 await fetchBranches();
 }
 } catch (error) {
 console.error('Error deleting branch:', error);
 toast.error(error.response?.data?.detail || 'Gagal menghapus cabang');
 }
 };

 if (loading) {
 return (
 <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-64">
 <Activity className="w-8 h-8 text-blue-600 animate-spin dark:text-blue-400" />
 </div>
 );
 }

 return (
 <div className="p-6 ">
 <div className="mb-6 flex items-center justify-between">
 <div>
 <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
 <Building2 className="w-7 h-7 text-blue-600 dark:text-blue-400" />
 Manajemen Cabang
 </h1>
 <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
 Kelola cabang dan assign router per lokasi
 </p>
 </div>
 <Button
 onClick={() => {
 setEditingBranch(null);
 resetForm();
 setShowAddModal(true);
 }}
 variant="outline"
 className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white border-0"
 >
 <Plus className="h-4 w-4" />
 Tambah Cabang
 </Button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Cabang</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">{branches.length}</p>
 </div>
 <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
 <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Router</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">
 {branches.reduce((sum, b) => sum + (b.router_count || 0), 0)}
 </p>
 </div>
 <div className="p-3 bg-green-100 dark:bg-green-900/50 rounded-lg">
 <Server className="w-6 h-6 text-green-600 dark:text-green-400" />
 </div>
 </div>
 </CardContent>
 </Card>

 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-gray-600 dark:text-gray-400">Total Customer</p>
 <p className="text-2xl font-bold text-gray-900 dark:text-white">
 {branches.reduce((sum, b) => sum + (b.customer_count || 0), 0)}
 </p>
 </div>
 <div className="p-3 bg-purple-100 dark:bg-purple-900/50 rounded-lg">
 <Users className="w-6 h-6 text-purple-600 dark:text-purple-400" />
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {branches.length === 0 ? (
 <Card className="bg-white dark:bg-slate-800">
 <CardContent className="p-12 text-center">
 <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
 <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Belum Ada Cabang</h3>
 <p className="text-gray-600 dark:text-gray-400 mb-4">Tambahkan cabang pertama untuk mulai mengelola lokasi</p>
 <Button
 onClick={() => {
 setEditingBranch(null);
 resetForm();
 setShowAddModal(true);
 }}
 className="bg-blue-600 hover:bg-blue-700"
 >
 <Plus className="w-4 h-4 mr-2" />
 Tambah Cabang
 </Button>
 </CardContent>
 </Card>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {branches.map((branch) => (
 <Card key={branch.id} className="hover:shadow-lg transition-shadow">
 <CardHeader className="pb-3">
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-2 flex-wrap">
 <h3 className="text-lg font-bold text-gray-900 dark:text-white">{branch.name}</h3>
 {branch.is_active ? (
 <Badge className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400 text-xs">Aktif</Badge>
 ) : (
 <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-xs">Non-Aktif</Badge>
 )}
 <button
   type="button"
   onClick={() => togglePublicStore(branch)}
   className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
     (branch.is_public_store === undefined ? true : !!branch.is_public_store)
       ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800'
       : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
   }`}
   title={(branch.is_public_store === undefined ? true : !!branch.is_public_store)
     ? 'Tampil di Voucher Store — klik untuk sembunyikan'
     : 'Tersembunyi dari Voucher Store — klik untuk tampilkan'}
   data-testid={`toggle-public-store-${branch.id}`}
 >
   <ShoppingBag className="w-3 h-3" />
   {(branch.is_public_store === undefined ? true : !!branch.is_public_store) ? 'Store: ON' : 'Store: OFF'}
 </button>
 <button
   type="button"
   onClick={() => toggleWhatsApp(branch)}
   className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
     (branch.wa_enabled === undefined ? true : !!branch.wa_enabled)
       ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
       : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
   }`}
   title={(branch.wa_enabled === undefined ? true : !!branch.wa_enabled)
     ? 'Notifikasi WA aktif (registrasi, pembayaran, pengingat) - klik untuk matikan'
     : 'Notifikasi WA nonaktif - klik untuk aktifkan'}
   data-testid={`toggle-wa-${branch.id}`}
 >
   <MessageCircle className="w-3 h-3" />
   {(branch.wa_enabled === undefined ? true : !!branch.wa_enabled) ? 'WA: ON' : 'WA: OFF'}
 </button>
 </div>
 <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
 <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
 <span>{branch.address || '-'}</span>
 </div>
 {branch.description && (
 <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{branch.description}</p>
 )}
 </div>
 </div>
 </CardHeader>

 <CardContent className="pt-0">
 {/* Link Daftar Publik */}
 <div className="flex items-center gap-2 py-2 px-2 rounded-md bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 mb-2">
 <Link2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
 <div className="flex-1 min-w-0">
 <p className="text-[10px] uppercase tracking-wide text-indigo-500 dark:text-indigo-400">Link Daftar Publik</p>
 <p className="text-xs font-mono text-indigo-700 dark:text-indigo-300 truncate" title={branch.slug ? getRegisterUrl(branch.slug) : 'Slug belum ada'}>
 {branch.slug ? `${REGISTER_HOST}/${branch.slug}` : '— slug belum tersedia —'}
 </p>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={() => copyRegisterLink(branch.slug)}
 disabled={!branch.slug}
 className="h-7 px-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border-indigo-200 dark:border-indigo-800"
 data-testid={`copy-register-link-${branch.id}`}
 title="Salin link pendaftaran publik"
 >
 <Copy className="h-3 w-3 mr-1" /> Copy
 </Button>
 </div>

 <div className="flex items-center justify-between py-3 border-t border-gray-100 dark:border-slate-700">
 <div className="flex items-center gap-4">
 <div className="text-center">
 <p className="text-xs text-gray-500 dark:text-gray-400">Router</p>
 <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{branch.router_count || 0}</p>
 </div>
 <div className="text-center">
 <p className="text-xs text-gray-500 dark:text-gray-400">Customer</p>
 <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{branch.customer_count || 0}</p>
 </div>
 </div>

 <div className="flex items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 onClick={() => handleEdit(branch)}
 className="text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
 >
 <Edit2 className="h-3 w-3" />
 </Button>
 <Button
 variant="outline"
 size="sm"
 onClick={() => {
 setBranchToDelete(branch);
 setShowDeleteModal(true);
 }}
 className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
 >
 <Trash2 className="h-3 w-3" />
 </Button>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}

 {showAddModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white dark:bg-slate-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
 <div className="p-6 border-b border-gray-200 dark:border-slate-700">
 <div className="flex items-center justify-between">
 <h2 className="text-xl font-bold text-gray-900 dark:text-white">
 {editingBranch ? 'Edit Cabang' : 'Tambah Cabang Baru'}
 </h2>
 <button
 onClick={() => {
 setShowAddModal(false);
 setEditingBranch(null);
 resetForm();
 }}
 className="text-gray-400 hover:text-gray-600"
 >
 <X className="h-5 w-5" />
 </button>
 </div>
 </div>

 <form onSubmit={handleSubmit}>
 <div className="p-6 space-y-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
 Nama Cabang *
 </label>
 <Input
 required
 type="text"
 placeholder="Contoh: Depok, Tangsel"
 value={formData.name}
 onChange={(e) => setFormData({ ...formData, name: e.target.value })}
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
 Alamat Lengkap *
 </label>
 <textarea
 required
 rows={3}
 placeholder="Alamat lengkap cabang"
 value={formData.address}
 onChange={(e) => setFormData({ ...formData, address: e.target.value })}
 className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 dark:text-white"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
 Kota / Kabupaten (untuk deteksi lokasi buyer)
 </label>
 <Input
 type="text"
 placeholder="cth: Cianjur, Depok, Bondowoso"
 value={formData.city}
 onChange={(e) => setFormData({ ...formData, city: e.target.value })}
 data-testid="input-city"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
 Koordinat GPS (Voucher Store)
 </label>
 <div className="grid grid-cols-2 gap-2">
 <Input
 type="text"
 placeholder="Latitude (cth: -6.821)"
 value={formData.latitude}
 onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
 data-testid="input-latitude"
 />
 <Input
 type="text"
 placeholder="Longitude (cth: 107.144)"
 value={formData.longitude}
 onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
 data-testid="input-longitude"
 />
 </div>
 <div className="mt-2 flex flex-wrap gap-2">
 <button
 type="button"
 onClick={() => {
   if (!navigator.geolocation) { toast.error('Browser tidak mendukung GPS'); return; }
   navigator.geolocation.getCurrentPosition(
     (pos) => {
       setFormData((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(7), longitude: pos.coords.longitude.toFixed(7) }));
       toast.success('Koordinat diisi dari GPS Anda');
     },
     () => toast.error('Gagal mendapatkan lokasi. Pastikan izin lokasi diaktifkan.'),
     { timeout: 8000, enableHighAccuracy: true }
   );
 }}
 className="text-xs px-3 py-1.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800"
 data-testid="btn-detect-location"
 >
 📍 Deteksi Lokasi Saya
 </button>
 {formData.latitude && formData.longitude && (
 <a
 href={`https://www.google.com/maps/search/?api=1&query=${formData.latitude},${formData.longitude}`}
 target="_blank"
 rel="noopener noreferrer"
 className="text-xs px-3 py-1.5 rounded bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-800"
 >
 🗺️ Buka di Google Maps
 </a>
 )}
 </div>
 <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1.5">
 Isi lat/lng agar buyer bisa auto-diarahkan ke cabang terdekat di voucher.kumarahotspot.com.
 Cara cepat: buka Google Maps, tap lokasi cabang lama-lama, salin angka koordinat.
 </p>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
 Deskripsi (Opsional)
 </label>
 <Input
 type="text"
 placeholder="Deskripsi singkat tentang cabang"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 />
 </div>

 <div className="flex items-center gap-2">
 <input
 type="checkbox"
 id="is_active"
 checked={formData.is_active}
 onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
 className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:text-blue-400 dark:border-slate-600"
 />
 <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-slate-200">
 Cabang Aktif
 </label>
 </div>

 <div className="flex gap-3 pt-4">
 <Button
 type="button"
 variant="outline"
 onClick={() => {
 setShowAddModal(false);
 setEditingBranch(null);
 resetForm();
 }}
 className="flex-1"
 >
 Batal
 </Button>
 <Button
 type="submit"
 disabled={saving}
 className="flex-1 bg-blue-600 hover:bg-blue-700"
 >
 {saving ? (
 <>
 <Activity className="h-4 w-4 mr-2 animate-spin" />
 Menyimpan...
 </>
 ) : (
 editingBranch ? 'Update' : 'Tambah'
 )}
 </Button>
 </div>
 </div>
 </form>
 </div>
 </div>
 )}

 {showDeleteModal && branchToDelete && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white rounded-xl max-w-md w-full dark:bg-slate-800">
 <div className="p-6 ">
 <div className="flex items-center gap-3 mb-4">
 <div className="p-3 bg-red-100 rounded-full dark:bg-red-900/40">
 <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
 </div>
 <div>
 <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">Hapus Cabang?</h2>
 <p className="text-sm text-gray-500 dark:text-slate-400">Tindakan ini tidak dapat dibatalkan</p>
 </div>
 </div>

 <div className="bg-gray-50 rounded-lg p-4 mb-4 dark:bg-slate-900">
 <p className="text-sm text-gray-700 mb-2 dark:text-slate-200">Anda akan menghapus cabang:</p>
 <p className="text-base font-semibold text-gray-900 dark:text-slate-100">{branchToDelete.name}</p>
 <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">{branchToDelete.address}</p>
 </div>

 {(branchToDelete.router_count > 0 || branchToDelete.customer_count > 0) && (
 <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 dark:bg-yellow-900/30">
 <p className="text-xs text-yellow-800">
 <strong>Perhatian:</strong> Cabang ini memiliki {branchToDelete.router_count} router 
 dan {branchToDelete.customer_count} customer. Pindahkan atau hapus terlebih dahulu.
 </p>
 </div>
 )}

 <div className="flex gap-3">
 <Button
 type="button"
 variant="outline"
 onClick={() => {
 setShowDeleteModal(false);
 setBranchToDelete(null);
 }}
 className="flex-1"
 >
 Batal
 </Button>
 <Button
 type="button"
 onClick={handleDelete}
 className="flex-1 bg-red-600 hover:bg-red-700 text-white"
 >
 Ya, Hapus
 </Button>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default Branches;
